import { useCallback, useRef } from 'react';
import { SubscribeAPI } from 'services/subscribe.api';

/**
 * Debounce helper : retourne une fonction qui n'exécute le callback
 * qu'après `delay` ms d'inactivité.
 * Le type de retour est "unwrapped" pour éviter Promise<Promise<...>>.
 */
type Awaited<T> = T extends PromiseLike<infer U> ? U : T;

function useDebouncedCallback<T extends (...args: any[]) => any>(
  callback: T,
  delay: number,
) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  return useCallback(
    (...args: Parameters<T>): Promise<Awaited<ReturnType<T>>> => {
      if (timerRef.current) clearTimeout(timerRef.current);
      return new Promise<Awaited<ReturnType<T>>>((resolve) => {
        timerRef.current = setTimeout(async () => {
          const result = await callback(...args);
          resolve(result);
        }, delay);
      });
    },
    [callback, delay],
  );
}

/**
 * Validation asynchrone avec debounce pour le champ email.
 * Retourne une string d'erreur ou undefined si valide.
 */
export function useEmailAsyncValidator(errorMessage: string) {
  const validate = useCallback(
    async (email: string): Promise<string | undefined> => {
      if (!email || email.length < 3) return undefined;
      try {
        const { data } = await SubscribeAPI.checkEmailExists(email);
        // data.status === true signifie que l'email est disponible
        return data.status ? undefined : errorMessage;
      } catch {
        return errorMessage;
      }
    },
    [errorMessage],
  );

  return useDebouncedCallback(validate, 500);
}

/**
 * Validation asynchrone avec debounce pour le champ matricule.
 */
export function useMatriculeAsyncValidator(errorMessage: string) {
  const validate = useCallback(
    async (clientId: string): Promise<string | undefined> => {
      if (!clientId || clientId.length < 1) return undefined;
      try {
        const { data } = await SubscribeAPI.checkMatriculeExists(clientId);
        return data.status ? undefined : errorMessage;
      } catch {
        return errorMessage;
      }
    },
    [errorMessage],
  );

  return useDebouncedCallback(validate, 500);
}

/**
 * Validation asynchrone avec debounce pour le champ code parrainage.
 */
export function useReferralCodeAsyncValidator(
  formatErrorMessage: string,
  notExistErrorMessage: string,
) {
  const validate = useCallback(
    async (referralCode: string): Promise<string | undefined> => {
      if (!referralCode) return undefined;
      // Vérifier le format d'abord
      if (!/^\d+-[A-Z0-9]{8}$/.test(referralCode)) {
        return formatErrorMessage;
      }
      try {
        const { data } = await SubscribeAPI.checkReferralCode(referralCode);
        return data.message ? undefined : notExistErrorMessage;
      } catch {
        return notExistErrorMessage;
      }
    },
    [formatErrorMessage, notExistErrorMessage],
  );

  return useDebouncedCallback(validate, 500);
}
